<script setup lang="ts">
  import type { Ref } from 'vue'
  import { useShare } from '@vueuse/core'
  import type { Block } from '@contentful/rich-text-types'
  import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
  import type { Recipe } from '@/types/Recipe'

  const route = useRoute()
  const slug = computed(() => route.params.slug as string)
  const search = inject<Ref<string>>('searchInput')

  let recipe = ref<Recipe | null>(null)
  if (slug?.value) recipe.value = useRecipe(slug?.value)?.data?.value

  const shareOptions = computed(() => ({
    title: `${recipe.value?.title} ${recipe.value?.subtitle || ''}`,
    text: recipe.value?.intro ? documentToPlainTextString(recipe.value.intro as Block) : undefined,
    url: window.location.origin + route.fullPath,
  }))

  const { share, isSupported } = useShare(shareOptions)
  const showShareButton = computed(() => {
    return typeof window !== 'undefined' && isSupported.value && slug?.value && recipe?.value
  })

  function clearSearch() {
    if (search?.value) search.value = ''
  }
</script>

<template>
  <nav>
    <h1>
      <NuxtLink to="/">
        <img src="/logo-192.png" alt="juss recipes" class="header__logo" />
        <span class="header__title">juss recipes</span>
      </NuxtLink>
    </h1>

    <div class="flex items-center">
      <div v-if="!route.path.includes('recipe')" class="header__searchBoxWrapper">
        <input v-model="search" type="text" class="header__searchBox text--smol" />

        <button v-if="search && search.length" class="header__searchClear" @click="clearSearch()">
          <i class="far fa-times"></i>
        </button>
      </div>

      <button v-if="showShareButton" class="header__button" @click="share()">
        <i class="fad fa-angle-double-up"></i>
        share
      </button>

      <NuxtLink to="/cart">
        <i
          class="far fa-shopping-cart header__cart"
          :class="{ 'header__cart--active': route.path.includes('/cart') }"
        ></i>
      </NuxtLink>
    </div>
  </nav>
</template>

<style lang="sass">
  nav
    background: theme('colors.juss.purple.DEFAULT')
    margin: 0 auto
    margin-bottom: 1.45rem
    padding: 10px 12px 10px 18px
    height: 60px
    display: flex
    align-items: center
    justify-content: space-between
    color: theme('colors.white')
    @media (min-width: 800px)
      padding: 10px 20px 10px 25px

    h1
      font-weight: 700
      margin: 0
      a
        display: flex
        text-decoration: none

    .header__logo
      height: 40px
      margin-right: 10px

    .header__title
      font-family: Mansalva
      color: #e6f1f9
      text-decoration: none
      font-size: 32px
      margin-top: -5px
      @media (max-width: 505px)
        display: none

  .header__searchBoxWrapper
    position: relative
    &::before
      content: '\F002'
      position: absolute
      top: 0
      left: 1em
      bottom: 0
      display: flex
      align-items: center
      font-family: 'Font Awesome 6 Pro'
      color: theme('colors.juss.purple.200')
      font-size: .8em
      font-weight: 400

    .header__searchBox
      font-family: "Quicksand", sans-serif
      font-size: 16px
      padding: 4px 26px 4px 30px
      border: 2px solid theme('colors.juss.purple.200')
      border-radius: 25px
      width: 220px
      background-color: #5d4871
      color: #fff
      transition: 150ms ease-in-out
      @media (max-width: 400px)
        width: 200px
      @media (min-width: 800px)
        width: 250px
      &:focus
        outline: none
        border-color: #d2c2e2

    .header__searchClear
      position: absolute
      right: 0
      top: 0
      bottom: 0
      -webkit-appearance: none
      background: transparent
      border: none
      color: rgba(white, 0.5)
      padding: 0 12px
      font-size: 16px
      cursor: pointer
      transition: 150ms ease-in-out
      &:hover
        color: white
      &:focus
        outline: none

  .header__button
    color: #5d4871
    background-color: #d9cfe4
    border-radius: 3px
    text-transform: uppercase
    font-size: 0.65em
    font-weight: 600
    cursor: pointer
    padding: 8px 12px
    appearance: none
    border: none
    transition: 150ms ease-in-out
    &:hover
      background-color: theme('colors.juss.purple.200')

  .header__cart
    color: theme('colors.juss.purple.200')
    font-size: 20px
    padding: 5px
    margin-left: 8px
    transition: background-color 150ms ease-in-out, color 150ms ease-in-out
    border-radius: 100px
    &--active
      display: flex
      align-items: center
      justify-content: center
      width: 36px
      height: 36px
      background-color: theme('colors.juss.purple.200')
      color: #5d4871
</style>
